import { ADMIN_ROLES } from '../utils/roleHelpers';
/* ── LeaveApprovalsPage ── */
export const LEAVE_APPROVALS_STRINGS = {
  BANNER_TITLE: 'Leave Approvals',
  BANNER_SUBTITLE: 'Review and action employee leave requests',
  TEAM_LEAVE_REQUESTS: 'Team Leave Requests',
  TEAM_LEAVE_SUB: 'Review and approve leave applications from your team members',
  ALERT_TEXT: 'Action required on pending leave applications.',
  BTN_APPROVE: 'Approve Leave',
  BTN_REJECT: 'Reject Leave',
  LABEL_REASON_REJECT: 'Reason for Rejection (Optional)',
  PLACEHOLDER_REASON: 'Enter reason for rejection...',
  BTN_RETRY: 'Retry',
  EMPTY_STATE_PREFIX: 'No ',
  EMPTY_STATE_SUFFIX: ' leave requests found.',
  LBL_SICK_LEAVE: 'Sick Leave',
  LBL_CASUAL_LEAVE: 'Casual Leave',
  TXT_DAY: 'day',
  TXT_DAYS: 'days',
  // Table Headers
  COL_EMPLOYEE: 'Employee',
  COL_LEAVE_TYPE: 'Leave Type',
  COL_DATE_RANGE: 'Date Range',
  COL_DURATION: 'Duration',
  COL_APPLIED_ON: 'Applied On',
  COL_STATUS: 'Status',
  COL_ACTIONS: 'Actions'
};

/* ── LeaveReportsPage ── */
export const LEAVE_REPORTS_STRINGS = {
  EMPTY_DASH: '-',
  BANNER_TITLE: 'Reports & Analytics',
  BANNER_SUBTITLE: 'View leave trends and workforce insights',
  PAGE_TITLE: 'Leave Reports',
  PLACEHOLDER_SEARCH: 'Search by name, ID, department...',
  PLACEHOLDER_EMP_ID: 'Employee ID (Exact)',
  LABEL_FROM_DATE: 'From Date',
  LABEL_TO_DATE: 'To Date',
  OPT_SELECT: 'Select',
  OPT_LEAVE_TYPE: 'Leave Type',
  OPT_STATUS: 'Status',
  OPT_SICK: 'Sick',
  OPT_CASUAL: 'Casual',
  OPT_PENDING: 'Pending',
  OPT_APPROVED: 'Approved',
  OPT_REJECTED: 'Rejected',
  BTN_RESET: 'Reset',
  BTN_DOWNLOAD: 'Download',
  EMPTY_STATE: 'No leave records found. Apply filters and click Filter to search.',
  PAGINATION_ROWS_PER_PAGE: 'Rows per page:',
  PAGINATION_0_RECORDS: '0 records',
  PAGINATION_OF: 'of',
  // Table Headers
  COL_EMP_NAME: 'Employee Name',
  COL_EMP_ID: 'Employee ID',
  COL_DEPT: 'Department',
  COL_LEAVE_TYPE: 'Leave Type',
  COL_APPLIED_DATE: 'Applied Date',
  COL_FROM_DATE: 'From Date',
  COL_TO_DATE: 'To Date',
  COL_DAYS: 'Days',
  COL_STATUS: 'Status',
  COL_LOP_DAYS: 'LOP Days',
  COL_APPROVED_BY: 'Approved/Rejected By'
};

/* ── ConsolidateLeaveReport ── */
export const CONSOLIDATE_REPORT_STRINGS = {
  BANNER_TITLE: 'Consolidate Leave Report',
  BANNER_SUBTITLE: 'Summary of employee leave across the organisation',
  PAGE_TITLE: 'Consolidate Leave Reports',
  PLACEHOLDER_MONTH: 'Month (YYYY-MM)',
  PLACEHOLDER_YEAR: 'Year (YYYY)',
  OPT_SELECT: 'Select',
  BTN_FILTER: 'Filter',
  BTN_RESET: 'Reset',
  BTN_DOWNLOAD: 'Download',
  EMPTY_STATE: 'No data found. Select filters and click Filter to generate the report.',
  // Table Headers
  COL_EMP_NAME: 'Employee Name',
  COL_EMP_ID: 'Employee ID',
  COL_DEPT: 'Department',
  COL_TOTAL_APPLIED: 'Total Leaves Applied',
  COL_TOTAL_DAYS: 'Total Leave Days',
  COL_APPROVED: 'Approved',
  COL_PENDING: 'Pending',
  COL_REJECTED: 'Rejected',
  COL_CL_USED: 'CL Used',
  COL_SL_USED: 'SL Used',
  COL_LOP_DAYS: 'LOP Days',
  COL_CL_BAL: 'CL Balance',
  COL_SL_BAL: 'SL Balance'
};

/* ── PolicyManagementPage ── */
export const POLICY_MANAGEMENT_STRINGS = {
  PAGE_TITLE: 'Policy Management',
  PAGE_SUBTITLE: 'Configure company policies, leave rules, and holiday schedules',
  TABLE_TITLE: 'Leave Policy Management',
  STAT_TOTAL_POLICIES: 'Total Policies',
  STAT_ACTIVE_POLICIES: 'Active Policies',
  STAT_LEAVE_TYPES: 'Leave Types',
  SEC_TITLE_POLICIES: 'Leave Policies',
  BTN_REFRESH: 'Refresh',
  BTN_CREATE: 'Create Policy',
  COL_LEAVE_TYPE: 'Leave Type',
  COL_MONTHLY_ACCRUAL: 'Monthly Accrual',
  COL_MAX_CARRY: 'Max Carry Forward',
  COL_APPLICABLE_TO: 'Applicable To',
  COL_ACTIONS: 'Actions',
  MODAL_CREATE_TITLE: 'Create Leave Policy',
  MODAL_EDIT_TITLE: 'Edit Leave Policy',
  HINT_NO_CARRY_LIMIT: 'Leave empty for no carry-forward limit',
  LBL_APPLICABLE_TO: 'Applicable To',
  APPLICABLE_TO_ALL: 'All Employees',
  SAVE_BTN_UPDATE: 'Update Policy',
  SAVE_BTN_CREATE: 'Create Policy',
  BTN_CANCEL: 'Cancel',
  // Form Labels & Options
  LBL_LEAVE_TYPE: 'Leave Type',
  LBL_MONTHLY_ACCRUAL: 'Monthly Accrual (days)',
  LBL_MAX_CARRY: 'Max Carry Forward (days)',
  OPT_SELECT_LEAVE: 'Select leave type...',
  OPT_CASUAL_LEAVE: 'Casual Leave',
  OPT_SICK_LEAVE: 'Sick Leave',
  LBL_SICK: 'Sick',
  LBL_CASUAL: 'Casual'
};

/* ── Leave Management Shared Constants ── */
export const TAB_KEYS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
} as const;

export type TabKey = typeof TAB_KEYS[keyof typeof TAB_KEYS];

export const TAB_LABELS: Record<TabKey, string> = {
  [TAB_KEYS.PENDING]: 'PENDING APPROVAL',
  [TAB_KEYS.APPROVED]: 'APPROVED',
  [TAB_KEYS.REJECTED]: 'REJECTED',
};

export const BADGE_COLORS: Record<TabKey, string> = {
  [TAB_KEYS.PENDING]: '#E8503A',
  [TAB_KEYS.APPROVED]: '#34D399',
  [TAB_KEYS.REJECTED]: '#EF4444',
};

export const ERROR_MESSAGES = {
  PAST_DATE_EXCEEDED: 'Date cannot be more than 5 business days in the past.',
  FROM_DATE_REQUIRED: 'From date is required',
  TO_DATE_REQUIRED: 'To date is required',
  REASON_REQUIRED: 'Reason is required',
  TO_DATE_AFTER_FROM: 'To date must be after From date',
  HALF_DAY_PERIOD_REQUIRED: 'Please select Morning or Afternoon for half day'
};

export const INFO_MESSAGES = {
  APPROVER_INFO_PREFIX: 'Your leave request will be sent to',
  APPROVER_INFO_SUFFIX: 'for approval.',
  ALREADY_APPLIED: 'Already taking leave on'
};
export const POLICY_STRINGS = {
  PAGE_TITLE: 'Policy Management',
  PAGE_SUBTITLE: 'Configure company policies, leave rules, and holiday schedules',
  TABLE_TITLE: 'Leave Policy Management',
  MODAL_TITLE_CREATE: 'Create Leave Policy',
  MODAL_TITLE_EDIT: 'Edit Leave Policy',
  APPLICABLE_TO_ALL: 'All Employees',
  SAVE_BTN_UPDATE: 'Update Policy',
  SAVE_BTN_CREATE: 'Create Policy',
};

export const EM_STRINGS = {
  LEAVE_INFO_TAB_TITLE: 'Leave Information',
  DAYS_LEFT: 'days left',
  USED: 'used',
  ACCRUED: 'accrued',
  RECENT_REQUESTS: 'RECENT LEAVE REQUESTS',
  DAYS_AVAILABLE: 'days available',
  CARRIED: 'carried',
  GO_TO_APPROVALS: 'Go to Approvals',
  BACK: 'Back',
  BACK_TO_APPROVALS: 'Back to Approvals',
};

export const LEAVE_LIST_STRINGS = {
  BANNER_TITLE: 'Attendance & Leaves',
  BANNER_SUBTITLE: 'View and manage leave requests and approvals',
  PAGE_TITLE: 'Leave Management',
  BTN_APPLY: 'Apply Leave',
  PLACEHOLDER_STATUS: 'All Statuses',
  OPT_PENDING: 'Pending',
  OPT_APPROVED: 'Approved',
  OPT_REJECTED: 'Rejected',
  PLACEHOLDER_LEAVE_TYPE: 'All Leave Types',
  OPT_SICK: 'Sick',
  OPT_CASUAL: 'Casual',
  LBL_MONTH_MM_YYYY: 'Month',
  COL_EMPLOYEE: 'Employee',
  COL_DATE_RANGE: 'Date Range',
  COL_DAYS: 'Days',
  COL_LEAVE_TYPE: 'Leave Type',
  COL_STATUS: 'Status',
  COL_APPLIED_DATE: 'Applied Date',
  COL_ACTIONS: 'Actions',
  EMPTY_STATE: 'No leaves found matching the filters.',
  ACTION_VIEW_DETAILS: 'View Details',
  BTN_CANCEL: 'Cancel',
  YTD_CREDITED: 'Leave Credited (YTD)',
  YTD_USED: 'Leave Used (YTD)',
  YTD_LOP: 'Loss of Pay (YTD)',
  CURRENT_MONTH: 'Current Month',
  CREDITED: 'Credited',
  USED: 'Used',
  LABEL_LEAVE_TYPE: 'Leave Type',
  OPT_CASUAL_LEAVE: 'Casual Leave',
  OPT_SICK_LEAVE: 'Sick Leave',
  LABEL_DURATION: 'Duration',
  OPT_FULL_DAY: 'Full Day',
  OPT_HALF_DAY: 'Half Day',
  LABEL_HALF_DAY_PERIOD: 'Half Day Period',
  OPT_SELECT_PERIOD: 'Select Period',
  OPT_MORNING: 'Morning (First Half)',
  OPT_AFTERNOON: 'Afternoon (Second Half)',
  LABEL_DATE: 'Date',
  LABEL_FROM_DATE: 'From Date',
  LABEL_TO_DATE: 'To Date',
  HINT_SELECT_FROM_FIRST: 'Please select a From Date first',
  LABEL_REASON: 'Reason',
  BTN_SUBMIT_REQUEST: 'Submit Request',
  ERR_REASON_MIN_LENGTH: 'Reason must be minimum ten characters',
  BTN_SUBMITTING: 'Submitting...',
};

/* ── Leave Sidebar Nav Items ── */
export const NAV_LEAVE_MANAGEMENT = {
  label: 'Leave Management',
  icon: 'event_available',
  path: '/attendance/leaves',
  roles: ['admin', 'employee', 'super_admin'] as string[],
} as const;

export const NAV_LEAVE_POLICIES = {
  label: 'Leave Policies',
  icon: 'policy',
  path: '/settings/policy-management',
  roles: ADMIN_ROLES,
} as const;

export const NAV_LEAVE_REPORTS = {
  label: 'Leave Reports',
  icon: 'assessment',
  path: '/reports/leaves',
  groupOwnerOrRoles: ADMIN_ROLES,
} as const;

export const NAV_CONSOLIDATE_LEAVE_REPORTS = {
  label: 'Consolidate Leave Reports',
  icon: 'analytics',
  path: '/reports/consolidate',
  groupOwnerOrRoles: ADMIN_ROLES,
  multiLine: true,
} as const;

export const NAV_LEAVE_APPROVALS = {
  label: 'Leave Approvals',
  icon: 'approval',
  path: '/attendance/leaves/approvals',
  groupOwnerOrRoles: ADMIN_ROLES,
} as const;
